<!-- eslint-disable vue/no-mutating-props -->
<template>
  <b-modal
    v-bind="{ ...attrs, 'hide-header': !modalTitle && !$slots.header && !titleInputConfig }"
    :id="id"
    v-model="isShown"
    :no-close-on-backdrop="noCloseOnBackdrop || !easilyDismissable"
    :no-close-on-esc="noCloseOnEsc || !easilyDismissable"
    :dialog-class="dialogClasses"
    :modal-class="modalClasses"
    :body-class="bodyClasses"
    class="d-print-none"
    @keyup.tab="shouldShowFocusOutline = true"
    @click="shouldShowFocusOutline = false"
    @show="onShow"
    @shown="onShown"
    @hide="onHide"
    @hidden="onHidden"
    @close="onCancelFn"
    @cancel="onCancelFn"
  >
    <template #modal-header>
      <!-- manual height applied to maintain a consistent height between
            the state of showing the form input and the plain header -->
      <b-container
        class="d-flex align-items-center justify-content-between w-100"
        style="height: 45px"
      >
        <div class="flex-grow-1 mw-0">
          <slot name="header">
            <b-row>
              <b-col>
                <b-form-group
                  v-if="titleInputConfig && isTitleEditingAllowed && isEditingTitle"
                  class="mb-0"
                >
                  <b-form-input
                    :id="`${id}-name-input`"
                    ref="titleInputRef"
                    v-model="title"
                    :placeholder="titleInputConfig?.placeholder"
                    :state="titleInputConfig?.state"
                    :data-testid="titleInputConfig?.dataTestid"
                    trim
                    @blur="onModalTitleInputBlur"
                    @keyup.enter="$emit('confirm-title-input')"
                  />
                  <b-form-invalid-feedback
                    :id="`${id}-name-input-feedback`"
                    class="position-absolute"
                  >
                    {{ titleInputConfig.errorMessage }}
                  </b-form-invalid-feedback>
                </b-form-group>
                <span
                  v-else
                  :class="{ 'cursor-pointer': isTitleEditingAllowed }"
                  class="d-flex align-items-center"
                  @click="onTitleClick"
                >
                  <h4 class="text-primary text-ellipsis mb-0">{{ modalTitle }}</h4>
                  <icon
                    v-if="isTitleEditingAllowed"
                    icon="pen"
                    class="text-info ml-2"
                  />
                </span>
              </b-col>
              <slot name="header-button" />
            </b-row>
          </slot>
        </div>
        <!-- Don't show the overlay over the refresh button if we are already showing the overlay over everything -->
        <b-overlay
          v-if="showRefreshBtn"
          :show="refreshBusy && !shouldShowOverlay"
          :opacity="overlayOpacity"
        >
          <button
            class="close ml-n3"
            type="button"
            :disabled="refreshBusy"
            @click="onRefreshFn()"
          >
            <Icon
              class="d-block"
              icon="refresh"
              size="sm"
            />
          </button>
        </b-overlay>
        <div v-if="showXButton">
          <button
            class="close"
            type="button"
            :disabled="busy"
            style="font-size: 1.5rem; margin-left: 15px"
            @click="onCancelFn()"
          >
            <font-awesome-layers functional>
              <font-awesome-icon icon="fa-solid fa-xmark" />
            </font-awesome-layers>
          </button>
        </div>
      </b-container>
    </template>
    <b-container :class="bodyClasses">
      <div class="position-relative">
        <slot>
          <!-- default slot for modal content -->
        </slot>
        <button
          v-if="showXButton && !modalTitle && !$slots.header && !titleInputConfig"
          class="close position-absolute"
          type="button"
          :disabled="busy"
          style="font-size: 1.5rem; top: 0; right: -15px"
          @click="onCancelFn()"
        >
          <font-awesome-layers functional>
            <font-awesome-icon icon="fa-solid fa-xmark" />
          </font-awesome-layers>
        </button>
      </div>
    </b-container>
    <template #modal-footer>
      <b-container>
        <b-row no-gutters>
          <b-col>
            <transition
              name="fade-transform"
              mode="out-in"
            >
              <slot
                v-if="!showConfirmationSlot"
                name="footer"
                :busy="busy"
                :ok="onConfirmFn"
                :cancel="onCancelFn"
              >
                <div class="d-flex justify-content-between">
                  <StandardButton
                    :severity="ButtonSeverity.SECONDARY"
                    :size="ButtonSize.NORMAL"
                    :disabled="busy"
                    :label="cancelLabel ?? translate({ path: 'GLOBAL.CANCEL' })"
                    @click="onCancelFn"
                  />
                  <StandardButton
                    :size="ButtonSize.NORMAL"
                    :disabled="!isConfirmEnabled"
                    :loading="busy"
                    :label="confirmLabel ?? translate({ path: 'GLOBAL.APPLY' })"
                    @click="onConfirmFn"
                  />
                </div>
              </slot>
              <slot
                v-else-if="showConfirmationSlot"
                name="footer-confirmation"
                :busy="busy"
                :ok="onConfirmWarningFn"
                :cancel="onCancelWarningFn"
              >
                <div class="d-flex justify-content-between">
                  <StandardButton
                    :severity="ButtonSeverity.SECONDARY"
                    :size="ButtonSize.NORMAL"
                    :disabled="busy"
                    :label="cancelWarningLabel ?? translate({ path: 'GLOBAL.CANCEL' })"
                    @click="onCancelWarningFn"
                  />
                  <StandardButton
                    :size="ButtonSize.NORMAL"
                    :disabled="!isConfirmEnabled"
                    :loading="busy"
                    :label="confirmWarningLabel ?? translate({ path: 'GLOBAL.APPLY' })"
                    @click="onConfirmWarningFn"
                  />
                </div>
              </slot>
            </transition>
          </b-col>
        </b-row>
      </b-container>
    </template>
    <!-- it's weird to have this overlay covering nothing, but actually because
    we are using no-wrap, it doesn't need to. This automatically covers the modal -->
    <b-overlay
      no-wrap
      rounded
      :show="shouldShowOverlay"
      :opacity="overlayOpacity"
    />
  </b-modal>
</template>

<script lang="ts">
import useTranslation from '@/composables/useTranslation';
import { defineComponent, ref, PropType, computed, watch } from 'vue';
import { templateRef, useVModel } from '@vueuse/core';
import { ClassProp, handlePossibleClassPropTypes } from '@/utils/bootstrapVue';
import { ModalTitleConfig } from '@/types/ModalTitleConfig';
import { FontAwesomeLayers, FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useFeatureFlag } from '@/composables/useFeatureFlag';
import { ButtonSize, ButtonSeverity } from '@/types/StandardButtonTypes';

/**
 * TODO: add loading indicator and disable pointer events and keyup/keydown events
 */

/**
 * For full list of props and events, please see the official docs here
 * https://bootstrap-vue.org/docs/components/modal
 */

/**
 * When to use StandardModal:
 * - An optional title in the header.
 * - A close (cross) button in the header.
 * - A body section.
 * - CANCEL and APPLY buttons in the footer.
 *
 * When not to use StandardModal:
 * - The modal's style does not align with the StandardModal UI.
 * - For example, should use b-modal for modals like DisclaimerModal, as it follows a different design pattern.
 */
export default defineComponent({
  name: 'StandardModal',
  compatConfig: {
    COMPONENT_V_MODEL: false,
  },
  components: {
    FontAwesomeLayers,
    FontAwesomeIcon,
  },
  props: {
    /**
     * Controls open of the modal. This is available as `v-model` binding
     */
    modelValue: {
      type: Boolean,
      required: false,
      default: false,
    },
    id: {
      type: String,
      required: true,
    },
    /**
     * The title of the modal.
     *
     * If the title of the modal is editable, then pass the ref you want to edit
     * as this prop, which will then be updated.
     */
    modalTitle: {
      type: String,
      required: false,
    },
    /**
     * Prop used to control the behavior of editing the modal title.
     *
     * If the title should be editable, make sure to supply this prop.
     * If the title is not editable, or conditionally editable, then supply `null`.
     */
    titleInputConfig: {
      type: Object as PropType<ModalTitleConfig | null>,
      required: false,
      default: null,
    },
    isConfirmEnabled: {
      type: Boolean,
      required: false,
      default: true,
    },
    noCloseOnEsc: {
      type: Boolean,
      required: false,
      default: false,
    },
    noCloseOnBackdrop: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Allow user to dismiss the modal by clicking on the backdrop/ escape button
     * It controls the no-close-on-backdrop and no-close-on-esc properties in the
     * standard BootstrapVue modal
     */
    easilyDismissable: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * Gives our modals an option to not be dismissable on confirm
     * If true, the modal will close on confirm
     * If false, the modal will be only dismissable on cancel
     */
    dismissableOnConfirm: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * Show a loading overlay that covers the whole modal.
     *
     * Implies `busy` be true if this is true.
     */
    shouldShowOverlay: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If true, disable cancel/apply buttons in the footer and the x button on
     * top right.
     *
     * This does **not** give them a loading spinner.
     */
    busy: {
      type: Boolean,
      default: false,
    },
    /**
     * onConfirm is not required because you may overwrite the footer with
     * your own content
     *
     * Return false to prevent auto close dialog when confirm button clicked
     */
    onConfirm: {
      type: Function as PropType<() => Promise<boolean | void> | boolean | void>,
      required: false,
      default: () => {},
    },
    confirmLabel: {
      type: String,
      required: false,
      default: null,
    },
    onConfirmWarning: {
      type: Function as PropType<() => Promise<void> | void>,
      required: false,
      default: () => {},
    },
    confirmWarningLabel: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * onCancel IS required because even if you overwrite the footer, you are able
     * to close a modal in a couple different ways. We encourage cleaning up after
     * yourself when a modal is closed, so please don't use an empty function.
     */
    onCancel: {
      type: Function as PropType<() => Promise<void> | void>,
      required: true,
    },
    cancelLabel: {
      type: String,
      required: false,
      default: null,
    },
    onCancelWarning: {
      type: Function as PropType<() => Promise<void> | void>,
      required: false,
    },
    cancelWarningLabel: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * For some modals, for example modals where you can delete things,
     * we want to add an extra confirmation. This boolean controls
     * whether that confirmation should be shown.
     */
    shouldShowWarningConfirmationSlot: {
      type: Boolean,
      required: false,
    },
    modalClass: {
      type: [String, Object, Array] as PropType<ClassProp>,
      required: false,
    },
    bodyClass: {
      type: [String, Object, Array] as PropType<ClassProp>,
      required: false,
    },
    dialogClass: {
      type: [String, Object, Array] as PropType<ClassProp>,
      required: false,
    },
    /**
     * Shows a refresh button next to the close button on the dialog header.
     *
     * Used for admin console only. Reserved for tasks which cause a very heavy load on the database, where we increase
     * the stale time on vue-query and we let the user manually refresh. Do NOT use on client facing pages.
     *
     * For client facing pages, we should always let vue-query handle auto-refresh and design performant APIs.
     */
    showRefreshBtn: {
      type: Boolean,
      default: false,
    },
    /**
     * Set if it is currently refreshing. When true, a spinner is shown on top of the refresh button.
     */
    refreshBusy: {
      type: Boolean,
      default: false,
    },
    /**
     * Callback when the refresh button is pressed.
     */
    onRefresh: {
      type: Function,
      required: false,
      default: () => {},
    },
    overlayOpacity: {
      type: Number,
      required: false,
      default: 0.85,
    },
    /**
     * Exists for the HSBC Disclaimer Modal to not have an X button.
     * Will be removed after WAA-12843
     * @deprecated
     */
    showXButton: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  emits: ['show', 'shown', 'hide', 'hidden', 'update:modelValue', 'update:modal-title', 'confirm-title-input'],
  setup(props, { emit, attrs }) {
    const { translate } = useTranslation();

    const showConfirmationSlot = useVModel(props, 'shouldShowWarningConfirmationSlot');

    const titleInputRef = templateRef<HTMLInputElement>('titleInputRef');

    /**
     * When the child modal emits its events, we re-emit them so that any parent component
     * can hook into them as necessary
     */
    const onShow = () => emit('show');
    const onShown = () => {
      if (props.titleInputConfig?.shouldDefaultToEditMode) {
        isEditingTitle.value = true;

        setTimeout(() => {
          titleInputRef.value?.focus();
        });
      }

      emit('shown');
    };
    const onHide = () => emit('hide');
    const onHidden = () => emit('hidden');
    const onCancelFn = async () => {
      await props.onCancel();
      isShown.value = false;
    };
    const onRefreshFn = () => {
      props.onRefresh();
    };

    const onCancelWarningFn = async () => {
      if (props.onCancelWarning) await props.onCancelWarning();
      showConfirmationSlot.value = false;
    };

    const onConfirmFn = async () => {
      const result = await props.onConfirm();
      if (result === false) {
        return;
      }

      isShown.value = !props.dismissableOnConfirm;
    };

    const onConfirmWarningFn = async () => {
      await props.onConfirmWarning();
      isShown.value = !props.dismissableOnConfirm;
    };

    const isShown = useVModel(props, 'modelValue', emit, { passive: true });

    const title = useVModel(props, 'modalTitle', emit, { passive: true, eventName: 'update:modal-title' });

    /**
     * IMPORTANT: destructuring the attrs in this way breaks reactivity.
     * That said, I don't think it's important for our use case, so we can
     * do this here. (I believe destructuring will NOT break reactivity
     * when we move to Vue 3)
     */
    const shouldShowFocusOutline = ref(false);

    /**
     * Create and return this method so that the parent component can call it too
     */
    const show = () => {
      isShown.value = true;
    };

    /**
     * Create and return this method so that the parent component can call it too
     */
    const hide = () => {
      isShown.value = false;
    };

    const dialogClasses = computed((): (string | { [className: string]: boolean })[] => {
      return handlePossibleClassPropTypes(props.dialogClass ?? {});
    });

    const bodyClasses = computed((): (string | { [className: string]: boolean })[] => {
      return ['position-static', ...handlePossibleClassPropTypes(props.bodyClass ?? {})];
    });

    const modalClasses = computed((): (string | { [className: string]: boolean })[] => {
      return [
        {
          'no-focus-outline': !shouldShowFocusOutline.value,
        },
        'd-print-none',
        ...handlePossibleClassPropTypes(props.modalClass ?? {}),
      ];
    });

    const isEditingTitle = ref(false);

    const onModalTitleInputBlur = () => {
      if (props.titleInputConfig?.state === false) {
        return;
      }

      isEditingTitle.value = false;
    };

    // eslint-disable-next-line no-restricted-syntax
    watch(
      isEditingTitle,
      (newVal) => {
        if (!newVal) return;

        setTimeout(() => {
          titleInputRef.value?.focus();
        });
      },
      { immediate: true },
    );

    const { isReadOnlyPlatform } = useFeatureFlag();

    const isTitleEditingAllowed = computed(() => {
      return props.titleInputConfig && !props.busy && !isReadOnlyPlatform.value;
    });

    const onTitleClick = () => {
      if (!isTitleEditingAllowed.value) {
        return;
      }

      isEditingTitle.value = true;
    };

    return {
      translate,
      isShown,
      show,
      hide,
      shouldShowFocusOutline,

      showConfirmationSlot,
      onConfirmWarningFn,
      onCancelWarningFn,

      /**
       * pass all props / attrs and bind them to the original modal so that we don't have to
       * explicitly re-bind all the possible props.
       */
      props,
      attrs,
      onShow,
      onShown,
      onHide,
      onHidden,
      onCancelFn,
      onConfirmFn,
      onRefreshFn,

      dialogClasses,
      modalClasses,
      bodyClasses,

      isEditingTitle,
      onModalTitleInputBlur,
      title,
      isTitleEditingAllowed,
      onTitleClick,
      ButtonSize,
      ButtonSeverity,
    };
  },
});
</script>
